import React, { createContext, ReactNode, useCallback, useState } from 'react';

import { RecoveryAction } from '../../utils/errorHandler';

import { Toast, ToastType } from './Toast';

interface ToastData {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  recoveryActions?: RecoveryAction[];
  persistent?: boolean;
}

interface ToastContextType {
  showToast: (toast: Omit<ToastData, 'id'>) => string;
  showSuccess: (message: string, title?: string, duration?: number) => string;
  showError: (
    message: string,
    title?: string,
    recoveryActions?: RecoveryAction[],
    persistent?: boolean
  ) => string;
  showWarning: (message: string, title?: string, duration?: number) => string;
  showInfo: (message: string, title?: string, duration?: number) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

export type { ToastContextType };

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export { ToastContext };

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = generateId();
      const newToast: ToastData = { ...toast, id };

      setToasts(prevToasts => {
        const updatedToasts = [newToast, ...prevToasts];
        // Limit the number of toasts
        return updatedToasts.slice(0, maxToasts);
      });

      return id;
    },
    [generateId, maxToasts]
  );

  const showSuccess = useCallback(
    (message: string, title?: string, duration: number = 4000) => {
      return showToast({
        type: 'success',
        title,
        message,
        duration,
      });
    },
    [showToast]
  );

  const showError = useCallback(
    (
      message: string,
      title?: string,
      recoveryActions?: RecoveryAction[],
      persistent: boolean = false
    ) => {
      return showToast({
        type: 'error',
        title,
        message,
        recoveryActions,
        persistent,
        duration: persistent ? 0 : 8000, // Longer duration for errors
      });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, title?: string, duration: number = 6000) => {
      return showToast({
        type: 'warning',
        title,
        message,
        duration,
      });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, title?: string, duration: number = 5000) => {
      return showToast({
        type: 'info',
        title,
        message,
        duration,
      });
    },
    [showToast]
  );

  const removeToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-0 right-0 z-50 p-4 space-y-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              id={toast.id}
              type={toast.type}
              title={toast.title}
              message={toast.message}
              duration={toast.duration}
              recoveryActions={toast.recoveryActions}
              persistent={toast.persistent}
              onClose={removeToast}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
